import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Users, AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Sub-status options for "protocolo"
const SUB_STATUS_PROTOCOLO = [
  { value: '', label: 'Nenhum (apenas status principal)' },
  { value: 'acao_processo_protocolo', label: 'Ação em processo de protocolo' },
  { value: 'acao_protocolada', label: 'Ação protocolada' },
  { value: 'decisao_concedida', label: 'Decisão concedida' },
  { value: 'orgaos_notificados', label: 'Órgãos notificados' }
];

// Baixa items for "em_processo_baixa"
const BAIXA_ITEMS = [
  { key: 'serasa', label: 'SERASA' },
  { key: 'spc_brasil', label: 'SPC Brasil' },
  { key: 'boa_vista', label: 'SCPC Boa Vista' },
  { key: 'ieptb_nacional', label: 'IEPTB Nacional' },
  { key: 'ieptb_sp', label: 'IEPTB SP' }
];

export const BatchStatusUpdateModal = ({ period, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingCurrentStatus, setLoadingCurrentStatus] = useState(true);
  const [clientsCount, setClientsCount] = useState(period.client_count || 0);
  const [status, setStatus] = useState('protocolo');
  const [subStatus, setSubStatus] = useState('');
  
  // State for em_processo_baixa
  const [baixaItems, setBaixaItems] = useState({
    serasa: '',
    spc_brasil: '',
    boa_vista: '',
    ieptb_nacional: '',
    ieptb_sp: ''
  });
  
  // State for concluido
  const [conclusaoTexto, setConclusaoTexto] = useState('NADA CONSTA Serasa, liberado. Solicite o seu');

  // Fetch current status when modal opens
  useEffect(() => {
    const fetchCurrentStatus = async () => {
      try {
        const response = await axios.get(`${API}/periods/${period.id}/current-status`, getAuthHeaders());
        const data = response.data;
        
        setClientsCount(data.client_count || 0);
        
        if (data.status) {
          setStatus(data.status);
        }
        
        if (data.sub_status) {
          setSubStatus(data.sub_status);
        }
        
        if (data.baixa_items) {
          setBaixaItems(prev => ({
            ...prev,
            ...data.baixa_items
          }));
        }
        
        if (data.conclusao_texto) {
          setConclusaoTexto(data.conclusao_texto);
        }
      } catch (error) {
        console.error('Erro ao buscar status atual:', error);
        // Se falhar, usa os valores padrão
      } finally {
        setLoadingCurrentStatus(false);
      }
    };

    fetchCurrentStatus();
  }, [period.id]);

  const handleBaixaChange = (key, value) => {
    setBaixaItems(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async () => {
    if (!status) {
      toast.error('Selecione um status');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        status: status,
        sub_status: status === 'protocolo' && subStatus ? subStatus : null,
        baixa_items: status === 'em_processo_baixa' ? baixaItems : null,
        conclusao_texto: status === 'concluido' ? conclusaoTexto : null
      };

      const response = await axios.put(`${API}/periods/${period.id}/batch-status`, payload, getAuthHeaders());
      toast.success(`${response.data.updated_count} serviços atualizados com sucesso!`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status em lote');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="batch-status-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="batch-status-title">
            <Users className="w-5 h-5 text-primary" />
            Alterar Status em Lote
          </DialogTitle>
          <DialogDescription>
            Altere o status de todos os clientes do período selecionado
          </DialogDescription>
        </DialogHeader>

        {loadingCurrentStatus ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Carregando status atual...</span>
          </div>
        ) : (
        <div className="space-y-4">
          {/* Period Info */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{period.name}</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(period.start_date).toLocaleDateString('pt-BR')} - {new Date(period.end_date).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <Badge variant="secondary" className="text-lg" data-testid="batch-client-count">
                {clientsCount} cliente(s)
              </Badge>
            </div>
          </div>

          {/* Warning */}
          <Alert variant="destructive" className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Atenção:</strong> Esta ação irá alterar o status de <strong>todos os {clientsCount} clientes</strong> deste período.
            </AlertDescription>
          </Alert>

          {/* Status Selection */}
          <div className="space-y-3">
            <Label>Novo Status</Label>
            <RadioGroup value={status} onValueChange={setStatus} className="space-y-2">
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="protocolo" id="batch-protocolo" data-testid="batch-radio-protocolo" />
                <Label htmlFor="batch-protocolo" className="cursor-pointer flex-1">
                  <span className="font-medium">Protocolo</span>
                  <span className="text-xs text-muted-foreground block">Status inicial do processo</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="em_processo_baixa" id="batch-baixa" data-testid="batch-radio-baixa" />
                <Label htmlFor="batch-baixa" className="cursor-pointer flex-1">
                  <span className="font-medium">Em Processo de Baixa</span>
                  <span className="text-xs text-muted-foreground block">Processo em andamento nos órgãos</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="concluido" id="batch-concluido" data-testid="batch-radio-concluido" />
                <Label htmlFor="batch-concluido" className="cursor-pointer flex-1">
                  <span className="font-medium">Concluído</span>
                  <span className="text-xs text-muted-foreground block">Processo finalizado</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="revogada" id="batch-revogada" data-testid="batch-radio-revogada" />
                <Label htmlFor="batch-revogada" className="cursor-pointer flex-1">
                  <span className="font-medium text-red-600">Revogada</span>
                  <span className="text-xs text-muted-foreground block">Ação revogada judicialmente</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="cancelado" id="batch-cancelado" data-testid="batch-radio-cancelado" />
                <Label htmlFor="batch-cancelado" className="cursor-pointer flex-1">
                  <span className="font-medium text-gray-700">Cancelado</span>
                  <span className="text-xs text-muted-foreground block">Processo cancelado administrativamente</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Sub-status for Protocolo */}
          {status === 'protocolo' && (
            <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Label className="text-amber-800">Subtítulo do Protocolo (opcional)</Label>
              <Select value={subStatus} onValueChange={setSubStatus}>
                <SelectTrigger data-testid="batch-substatus-select">
                  <SelectValue placeholder="Selecione um subtítulo (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {SUB_STATUS_PROTOCOLO.map((option) => (
                    <SelectItem key={option.value || 'none'} value={option.value || 'none'}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Baixa Items for Em Processo de Baixa */}
          {status === 'em_processo_baixa' && (
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-blue-800">Status por Órgão</Label>
              <div className="space-y-2">
                {BAIXA_ITEMS.map((item) => (
                  <div 
                    key={item.key} 
                    className="flex items-center justify-between p-2 bg-card rounded border"
                    data-testid={`batch-baixa-item-${item.key}`}
                  >
                    <span className="font-medium text-sm">{item.label}</span>
                    <Select 
                      value={baixaItems[item.key] || 'none'} 
                      onValueChange={(value) => handleBaixaChange(item.key, value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="w-[130px]" data-testid={`batch-select-${item.key}`}>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-muted-foreground">Selecione</span>
                        </SelectItem>
                        <SelectItem value="baixando">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                            BAIXANDO
                          </span>
                        </SelectItem>
                        <SelectItem value="baixado">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            BAIXADO
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              
              {/* Summary badges */}
              {Object.values(baixaItems).some(v => v) && (
                <div className="flex flex-wrap gap-1 pt-2">
                  {Object.entries(baixaItems).map(([key, value]) => {
                    if (!value) return null;
                    const item = BAIXA_ITEMS.find(b => b.key === key);
                    return (
                      <Badge 
                        key={key} 
                        variant={value === 'baixado' ? 'default' : 'secondary'}
                        className={value === 'baixado' ? 'bg-green-600 text-xs' : 'bg-yellow-600 text-xs'}
                      >
                        {item?.label}: {value.toUpperCase()}
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Conclusão text for Concluído */}
          {status === 'concluido' && (
            <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <Label className="text-green-800">Mensagem de Conclusão</Label>
              <Textarea
                value={conclusaoTexto}
                onChange={(e) => setConclusaoTexto(e.target.value)}
                placeholder="Ex: NADA CONSTA Serasa, liberado. Solicite o seu"
                className="min-h-[80px] bg-card"
                data-testid="batch-conclusao-textarea"
              />
              <p className="text-xs text-green-700">
                Esta mensagem será exibida para todos os clientes quando o processo estiver concluído
              </p>
            </div>
          )}

          {/* Info block for Revogada */}
          {status === 'revogada' && (
            <div className="space-y-2 p-4 bg-red-50 rounded-lg border border-red-200" data-testid="batch-revogada-info">
              <Label className="text-red-800 font-semibold">Atenção: Status Revogada</Label>
              <p className="text-xs text-red-700">
                Todos os clientes deste período terão o processo marcado como <strong>Revogada</strong>.
                Sub-status, itens de baixa e textos de conclusão serão limpos.
              </p>
            </div>
          )}

          {/* Info block for Cancelado */}
          {status === 'cancelado' && (
            <div className="space-y-2 p-4 bg-gray-100 rounded-lg border border-gray-300" data-testid="batch-cancelado-info">
              <Label className="text-gray-800 font-semibold">Atenção: Status Cancelado</Label>
              <p className="text-xs text-gray-700">
                Todos os clientes deste período terão o processo marcado como <strong>Cancelado</strong>.
                Sub-status, itens de baixa e textos de conclusão serão limpos.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="batch-status-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !status}
              className="flex-1"
              data-testid="batch-status-submit"
            >
              {loading ? 'Atualizando...' : `Atualizar ${clientsCount} Cliente(s)`}
            </Button>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchStatusUpdateModal;
